import { HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import { generateSignalROptions } from "stores/RealTimeMessages/TaskStore";
import { TestLLMClassifierAgreementRequest } from "models/LLMs/Testing/TestLLMClassifierAgreementRequest";
import { TestLLMClassifierAgreementResponse } from "models/LLMs/Testing/TestLLMClassifierAgreementResponse";
import { TestLLMClassifierOnConversationsRequest } from "models/LLMs/Testing/TestLLMClassifierOnConversationsRequest";
import { TestLLMClassifierOnConversationsResponse } from "models/LLMs/Testing/TestLLMClassifierOnConversationsResponse";
import { AuthStore } from "stores/AuthStore";
import { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";
import RootStore from "stores/RootStore";
import BaseService, { ServiceError } from "./BaseService";

type AcceptedTest = {
    jobId: string;
    organizationId: string;
    statusUrl: string;
    cancelUrl: string;
    deadline: string;
};

export type TestSetLabelCount = {
    id: string;
    value: string;
    count: number;
};

export type ClassifierTestProgress = {
    state: "Queued" | "Running" | "Succeeded" | "Failed" | "Canceled" | "TimedOut";
    processedCount: number;
    receivedCount?: number;
    totalCount?: number | null;
    startedAt?: string | null;
    finishedAt?: string | null;
    cancellationRequested?: boolean;
    error?: string;
};

class TestLLMClassifierService extends BaseService {
    protected urlBase: string = "api/LLMClassifierTesting";

    public testLLMClassifierAgreement(
        request: TestLLMClassifierAgreementRequest,
        isTestOne: boolean = false,
        testOneLabel: string | undefined = undefined,
        abortSignal?: AbortSignal,
        onProgress?: (progress: ClassifierTestProgress, result?: TestLLMClassifierAgreementResponse) => void,
    ): Promise<TestLLMClassifierAgreementResponse> {
        const params = new URLSearchParams({ isTestOne: String(isTestOne) });
        if (testOneLabel !== undefined) params.set("isTestOneLabel", testOneLabel);
        return this.runTest<TestLLMClassifierAgreementResponse>("TestLLMClassifierAgreement", request, params,
            { processedCount: 0, agreementCount: 0, agreementPercent: 0, labelToResults: {}, noAgreementResults: [], errorResults: [] },
            (previous, batch) => {
                const processedCount = previous.processedCount + batch.processedCount;
                const agreementCount = previous.agreementCount + batch.agreementCount;
                return {
                    processedCount, agreementCount,
                    agreementPercent: processedCount ? Math.round(agreementCount / processedCount * 100) : 0,
                    labelToResults: appendGroups(previous.labelToResults, batch.labelToResults),
                    noAgreementResults: [...previous.noAgreementResults, ...batch.noAgreementResults],
                    errorResults: [...previous.errorResults, ...batch.errorResults],
                };
            }, abortSignal, onProgress);
    }

    public testLLMClassifierOnConversations(
        request: TestLLMClassifierOnConversationsRequest,
        abortSignal?: AbortSignal,
        onProgress?: (progress: ClassifierTestProgress, result?: TestLLMClassifierOnConversationsResponse) => void,
    ): Promise<TestLLMClassifierOnConversationsResponse> {
        return this.runTest<TestLLMClassifierOnConversationsResponse>(
            "TestLLMClassifierOnConversations",
            request,
            new URLSearchParams(),
            { processedCount: 0, decisionToResults: {}, noDecisionResults: [], errorResults: [] },
            (previous, batch) => ({
                processedCount: previous.processedCount + batch.processedCount,
                decisionToResults: appendGroups(previous.decisionToResults, batch.decisionToResults),
                noDecisionResults: [...previous.noDecisionResults, ...batch.noDecisionResults],
                errorResults: [...previous.errorResults, ...batch.errorResults],
            }),
            abortSignal,
            onProgress,
        );
    }

    /**
     * Counts the conversations matching the filters that were labeled with each answer option.
     */
    public getTestSetLabelCounts(
        questionId: string,
        filters: ApplicationFilters,
        abortSignal?: AbortSignal,
    ): Promise<TestSetLabelCount[]> {
        return this.post<TestSetLabelCount[]>(
            JSON.stringify({ questionId, filters }),
            new URLSearchParams(),
            "TestSetLabelCounts",
            undefined,
            undefined,
            abortSignal,
        );
    }

    /**
     * Counts the human labels for the question that an agreement test can use, per answer option.
     */
    public getAgreementLabelCounts(
        questionId: string,
        abortSignal?: AbortSignal,
    ): Promise<TestSetLabelCount[]> {
        return this.get<TestSetLabelCount[]>(
            new URLSearchParams({ questionId }),
            "AgreementLabelCounts",
            abortSignal,
        );
    }

    private async runTest<T extends { processedCount: number }>(
        path: string,
        request: unknown,
        params: URLSearchParams,
        emptyResult: T,
        merge: (previous: T, batch: T) => T,
        signal?: AbortSignal,
        onProgress?: (progress: ClassifierTestProgress, result?: T) => void,
    ): Promise<T> {
        signal?.throwIfAborted();
        const authStore = RootStore().getStore(AuthStore);
        // Keep the submitting organization even if the user switches organizations during the test.
        const headers = { ...authStore.getRequestHeader(), "api-version": this.apiVersion };
        const organizationId = headers["Acx-OrganizationId"];
        const subscriptionId = crypto.randomUUID();
        params.set("subscriptionId", subscriptionId);
        const connection = new HubConnectionBuilder()
            .withUrl(`/hubs/classifier-tests?ws_organizationId=${encodeURIComponent(organizationId)}`, generateSignalROptions(authStore))
            .withAutomaticReconnect({ nextRetryDelayInMilliseconds: context => Math.min(1000 * (context.previousRetryCount + 1), 30_000) })
            .build();
        let job: AcceptedTest | undefined;
        let stopped = false;
        let terminal = false;
        let accumulated = emptyResult;
        let status: ClassifierTestProgress | undefined;
        let lastMessageAt = Date.now();
        let refreshing = false;
        let resolve!: (result: T) => void;
        let reject!: (error: unknown) => void;
        const completed = new Promise<T>((resolve_, reject_) => { resolve = resolve_; reject = reject_; });
        // Cancellation/results may arrive while the initial HTTP submission is still returning.
        void completed.catch(() => undefined);
        const fail = (error: unknown) => { stopped = true; reject(error); };
        const abort = () => fail(new DOMException("Testing cancelled", "AbortError"));
        const receive = (next: ClassifierTestProgress, json?: string | null) => {
            if (stopped) return;
            lastMessageAt = Date.now();
            try {
                if (json) accumulated = merge(accumulated, JSON.parse(json) as T);
                // A status recovery request may overlap live updates. A terminal update always applies: when storage
                // fails, the server reports the failure from an older snapshot that may count fewer conversations.
                const finished = !["Queued", "Running"].includes(next.state);
                if (!status || finished || (next.processedCount >= status.processedCount && (!status.startedAt || next.startedAt))) {
                    status = { ...next, processedCount: Math.max(next.processedCount, status?.processedCount ?? 0) };
                }
                onProgress?.({ ...status, receivedCount: accumulated.processedCount }, json ? accumulated : undefined);
                if (["Queued", "Running"].includes(status.state)) return;
                terminal = true;
                stopped = true;
                if (status.state === "Succeeded") resolve(accumulated);
                else if (status.state === "Canceled") reject(new DOMException("Testing cancelled", "AbortError"));
                else reject(new Error(status.error ?? "The test could not be completed. Please run it again."));
            } catch (error) { fail(error); }
        };
        const refresh = async () => {
            if (stopped || refreshing) return;
            refreshing = true;
            try {
                if (connection.state === HubConnectionState.Disconnected) await connection.start();
                if (stopped || connection.state !== HubConnectionState.Connected) return;
                await connection.invoke("Subscribe", organizationId, subscriptionId);
                // Only recover small status metadata; missed results are deliberately not replayed.
                if (job) receive(await this.jobRequest<ClassifierTestProgress>(job.statusUrl, headers, "GET", undefined, signal));
            } finally { refreshing = false; }
        };
        let statusFailures = 0;
        const reconnect = () => {
            void refresh().then(() => { statusFailures = 0; }, error => {
                if (error instanceof Error && error.message.includes("HubException:")) fail(error);
                // Status recovery is the fallback when live updates stop. Stop polling a status endpoint that keeps failing.
                else if (error instanceof ServiceError && error.response.status >= 500 && ++statusFailures >= 3) {
                    fail(new Error("The test status could not be loaded. Please run it again."));
                }
            });
        };
        connection.on("Progress", receive);
        connection.onreconnected(reconnect);
        connection.onclose(reconnect);
        const cancelOnPageExit = () => {
            if (terminal || !job) return;
            void fetch(job.cancelUrl, { method: "POST", headers, keepalive: true, cache: "no-store" }).catch(() => undefined);
        };
        window.addEventListener("pagehide", cancelOnPageExit);
        signal?.addEventListener("abort", abort, { once: true });
        // Running workers send progress periodically. Recover status only after a period of silence.
        const watchdog = window.setInterval(() => {
            if (job && Date.now() > Date.parse(job.deadline) + 30_000) {
                fail(new Error("The test did not finish in time. Please run it again."));
            } else if (job && Date.now() - lastMessageAt > 30_000) reconnect();
        }, 5000);
        try {
            // Subscribe first: there is no result buffer to cover a late subscription.
            await Promise.race([refresh(), completed]);
            signal?.throwIfAborted();
            // Submit once. Allow the response to finish after Cancel so we can cancel the accepted job.
            job = await this.jobRequest<AcceptedTest>(`/${this.urlBase}/${path}?${params}`, headers, "POST", JSON.stringify(request));
            return await completed;
        } finally {
            stopped = true;
            window.clearInterval(watchdog);
            signal?.removeEventListener("abort", abort);
            window.removeEventListener("pagehide", cancelOnPageExit);
            await connection.stop();
            // Transport failures leave execution running. Only explicit cancellation/editor exit stops it.
            if (job && !terminal && signal?.aborted) {
                await this.jobRequest(job.cancelUrl, headers, "POST").catch(() => undefined);
            }
        }
    }

    private async jobRequest<T>(
        url: string,
        headers: Record<string, string>,
        method: "GET" | "POST",
        body?: string,
        signal?: AbortSignal,
    ): Promise<T> {
        const timeout = new AbortController();
        const abort = () => timeout.abort(signal?.reason);
        signal?.addEventListener("abort", abort, { once: true });
        if (signal?.aborted) abort();
        const timer = window.setTimeout(() => timeout.abort(new DOMException("Request timed out", "TimeoutError")), 30_000);
        try {
            const response = await fetch(url, { method, headers, body, signal: timeout.signal, cache: "no-store" });
            if (!response.ok) {
                if (response.status === 401) await this.authFailureHandler?.();
                if (response.status === 404) {
                    throw new Error("The test is no longer available. Please run it again.");
                }
                throw new ServiceError(method, url, response.clone(), await response.text());
            }
            return await response.json();
        } finally {
            window.clearTimeout(timer);
            signal?.removeEventListener("abort", abort);
        }
    }
}

function appendGroups<T>(previous: Record<string, T[]> | undefined, batch: Record<string, T[]>): Record<string, T[]> {
    const groups = { ...previous };
    for (const [key, items] of Object.entries(batch)) {
        // Labels and decisions are user-defined, including names such as "constructor".
        const existing = Object.prototype.hasOwnProperty.call(groups, key) ? groups[key] : [];
        Object.defineProperty(groups, key, {
            value: [...existing, ...items], enumerable: true, configurable: true, writable: true,
        });
    }
    return groups;
}

export default TestLLMClassifierService;
